// Dashboard page — hero band greeting with portfolio KPIs, number-forward
// stat blocks, and permission-aware Generate / Manage widgets. Hero KPIs and
// stat values come from useDashboardMetrics, which aggregates fully paginated
// lists: on partial or failed pagination the KPI row is omitted and stat
// blocks show an explicit unavailable state — never a partial number.
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Plus, FileText, CheckCircle, Clock, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCurrentOrganisation } from "../hooks";
import { useProfile } from "../hooks/useProfile";
import { formatDayMonthYear } from "../utils/formatShortDate";
import {
  useDashboardMetrics,
  type DashboardMetricsStatus,
} from "../hooks/useDashboardMetrics";
import {
  useExpiringContracts,
  useRecentManagedContracts,
} from "../hooks/useManagedContracts";
import {
  Card,
  Button,
  Badge,
  LoadingSpinner,
  HeroBand,
  StatBlock,
} from "../components/common";

// Shared row-link styling for the four dashboard list widgets (recent drafts,
// recent generated, expiring soon, recently updated). The focus-visible ring
// gives keyboard users the same row affordance pointer users get from hover.
const ROW_LINK_CLASS =
  "flex items-center justify-between py-4 hover:bg-slate-50 dark:hover:bg-slate-800 -mx-6 px-6 transition-colors focus-visible:bg-slate-50 dark:focus-visible:bg-slate-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400";

// Maps a metrics status + complete values onto the StatBlock state contract:
// loading → skeleton, error → explicit unavailable, success → value with its
// trend text (the accessible counterpart of the decorative sparkline).
function pluralise(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function statBlockState(
  status: DashboardMetricsStatus,
  count: number | undefined,
  series: number[] | undefined,
  trendWord: string,
): { value: ReactNode; sub: string; series?: number[] } {
  if (status === "error") {
    return { value: "—", sub: "Couldn't load metrics" };
  }
  if (status !== "success" || count === undefined) {
    return {
      value: (
        <span
          aria-hidden="true"
          className="inline-block h-9 w-16 animate-pulse rounded bg-slate-200 dark:bg-slate-700"
        />
      ),
      sub: "Loading…",
    };
  }
  const recent = series?.reduce((a, b) => a + b, 0) ?? 0;
  return {
    value: count,
    sub: `${recent} ${trendWord} in the last 12 weeks`,
    series,
  };
}

export default function Dashboard() {
  const { user, hasArea } = useAuth();
  const { data: organisation } = useCurrentOrganisation();
  const { data: profile } = useProfile();
  const { generate, manage } = useDashboardMetrics();

  const showGenerate = hasArea("generate");
  const showManage = hasArea("manage");

  // Same display-name resolution as the Layout sidebar: the profile's set
  // name, falling back to the email local part while the profile loads or
  // when it has no name.
  const greetingName = profile?.name || user?.email?.split("@")[0];
  const manageReady = showManage && manage.status === "success";

  // Manage users see live portfolio context; the line is omitted (not
  // approximated) while manage metrics are loading or failed. Generate-only
  // users get a static line.
  const subtitle = showManage
    ? manageReady
      ? `${pluralise(manage.contractCount ?? 0, "contract")} under management · ${manage.expiringWithin90Days} expiring within 90 days`
      : undefined
    : "Create, configure and generate contract documents.";

  const kpis = manageReady
    ? [
        {
          label: "Portfolio value",
          value: manage.portfolioValueFormatted,
          meta: `across ${pluralise(manage.supplierCount ?? 0, "supplier")}`,
        },
        {
          label: "Active contracts",
          value: String(manage.activeCount),
          meta: `of ${manage.contractCount} total`,
        },
        {
          label: "New · last 12 weeks",
          meta: `${manage.newLast12WeeksCount} added`,
          series: manage.newLast12WeeksSeries,
        },
      ]
    : undefined;

  const totalStat = statBlockState(
    generate.status,
    generate.totalContracts,
    generate.totalSeries,
    "added",
  );
  const draftStat = statBlockState(
    generate.status,
    generate.draftCount,
    generate.draftSeries,
    "started",
  );
  const generatedStat = statBlockState(
    generate.status,
    generate.generatedCount,
    generate.generatedSeries,
    "new",
  );

  const isLoading = generate.status === "loading";
  const drafts = generate.recentDrafts ?? [];
  const generated = generate.recentGenerated ?? [];
  const hasNoContracts =
    generate.status === "success" && generate.totalContracts === 0;

  return (
    <div className="space-y-6">
      <HeroBand
        eyebrow={organisation?.name}
        title={greetingName ? `Welcome back, ${greetingName}` : "Welcome back"}
        subtitle={subtitle}
        action={
          showGenerate ? (
            <Link to="/generate/contracts/new">
              <Button
                icon={<Plus className="h-4 w-4" />}
                className="bg-white text-brand-700 hover:bg-brand-50 active:bg-brand-100 focus:ring-white/70 shadow-none hover:shadow-none dark:shadow-none dark:hover:shadow-none"
              >
                New Document Draft
              </Button>
            </Link>
          ) : undefined
        }
        kpis={kpis}
      />

      {/* Stats */}
      {showGenerate && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatBlock
            label="Total Contracts"
            value={totalStat.value}
            sub={totalStat.sub}
            series={totalStat.series}
            sparklineClassName="text-brand-600"
          />
          <StatBlock
            label="Drafts"
            value={draftStat.value}
            sub={draftStat.sub}
            series={draftStat.series}
            sparklineClassName="text-slate-400"
          />
          <StatBlock
            label="Generated"
            value={generatedStat.value}
            sub={generatedStat.sub}
            series={generatedStat.series}
            sparklineClassName="text-emerald-600"
          />
        </div>
      )}

      {/* Generate section — Recent Contracts */}
      {showGenerate && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-h3 text-slate-900 dark:text-slate-50">
              Recent Contracts
            </h2>
            <Link
              to="/generate/contracts"
              className="text-body-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : hasNoContracts ? (
            <Card>
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">
                  No contracts yet
                </p>
                <Link to="/generate/contracts/new">
                  <Button variant="secondary" className="mt-4">
                    Create your first contract
                  </Button>
                </Link>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Recent Drafts */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-h3 text-slate-900 dark:text-slate-50">
                    Recent Drafts
                  </h3>
                </div>
                {drafts.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">
                      No drafts in progress
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {drafts.map((contract) => (
                      <Link
                        key={contract.id}
                        to={
                          contract.wizardStep !== "complete"
                            ? `/generate/contracts/new?resume=${contract.id}`
                            : `/generate/contracts/${contract.id}`
                        }
                        className={ROW_LINK_CLASS}
                      >
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-50">
                            {contract.title}
                          </p>
                          <p className="text-body-sm text-slate-500 dark:text-slate-400">
                            {contract.parties?.length
                              ? contract.parties.map((p) => p.name).join(" & ")
                              : "No parties yet"}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Badge status={contract.status} />
                          <span className="text-body-sm text-slate-500 dark:text-slate-400">
                            {new Date(contract.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </Card>

              {/* Recently Generated */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-h3 text-slate-900 dark:text-slate-50">
                    Recently Generated
                  </h3>
                </div>
                {generated.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">
                      No generated contracts yet
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {generated.map((contract) => (
                      <Link
                        key={contract.id}
                        to={`/generate/contracts/${contract.id}`}
                        className={ROW_LINK_CLASS}
                      >
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-50">
                            {contract.title}
                          </p>
                          <p className="text-body-sm text-slate-500 dark:text-slate-400">
                            {contract.parties?.length
                              ? contract.parties.map((p) => p.name).join(" & ")
                              : "No parties yet"}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Badge status={contract.status} />
                          <span className="text-body-sm text-slate-500 dark:text-slate-400">
                            {new Date(contract.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}
        </>
      )}

      {/* Manage section */}
      {showManage && <ManageSection />}
    </div>
  );
}

function ManageSection() {
  const { data: expiringData, isLoading: expiringLoading } =
    useExpiringContracts(90);
  const { data: recentData, isLoading: recentLoading } =
    useRecentManagedContracts(5);

  const isLoading = expiringLoading || recentLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <p className="text-overline text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        Manage
      </p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Expiring Soon */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h3 text-slate-900 dark:text-slate-50">
              Expiring Soon
            </h2>
          </div>
          {expiringData?.items.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                No contracts expiring soon
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {expiringData?.items.map((contract) => (
                <Link
                  key={contract.id}
                  to={`/manage/contracts/${contract.id}`}
                  className={ROW_LINK_CLASS}
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-50">
                      {contract.title}
                    </p>
                    <p className="text-body-sm text-slate-500 dark:text-slate-400">
                      {contract.supplierName}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge
                      variant={
                        contract.status === "active" ? "active" : "expired"
                      }
                      label={
                        contract.status.charAt(0).toUpperCase() +
                        contract.status.slice(1)
                      }
                    />
                    <span className="text-body-sm text-slate-500 dark:text-slate-400">
                      {formatDayMonthYear(contract.endDate)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Recently Updated */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-h3 text-slate-900 dark:text-slate-50">
              Recently Updated
            </h2>
          </div>
          {recentData?.items.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="h-12 w-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                Create your first managed contract
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {recentData?.items.map((contract) => (
                <Link
                  key={contract.id}
                  to={`/manage/contracts/${contract.id}`}
                  className={ROW_LINK_CLASS}
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-50">
                      {contract.title}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge
                      variant={
                        contract.status === "active" ? "active" : "draft"
                      }
                      label={
                        contract.status.charAt(0).toUpperCase() +
                        contract.status.slice(1)
                      }
                    />
                    <span className="text-body-sm text-slate-500 dark:text-slate-400">
                      {new Date(contract.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
